import * as basket from "../eaiws/basket";
import { ListenableEvent, Log } from "../utils";
import { chunk, clone, removeElement } from "../utils/array";
import { ArticleItem } from "./ArticleItem";
import { ItemUpdateFlags } from "./BaseItem";
import { BaseItemManager } from "./BaseItemManager";
import { BasketItem } from "./BasketItem";
import { CalculationManager } from "./CalculationManager";
import { ProjectInfo } from "./ProjectInfo";
import { FolderItem } from "./FolderItem";
import { SetArticleItem } from "./SetArticleItem";
import { UserArticleItem } from "./UserArticleItem";
import { TextItem } from "./TextItem";
import { View } from "./View";
import { BASKET_COLUMN_ID_EXT_ITEM_NUMBER, BASKET_COLUMN_ID_USER_IMAGE, BASKET_COLUMN_ID_COMMENTS, BASKET_COLUMN_ID_ADDITIONAL_IMAGES } from "./Constants";
export class Basket extends BaseItemManager {
    get calculation() {
        return (this.mCalcManager);
    }
    get views() {
        return (this.mViews);
    }
    get projectInfo() {
        return (this.mProjectInfo);
    }
    /**
     * Defines the image resolution for server generated images. (default: 512)
     */
    get generatedImageResolution() {
        return (this.mGeneratedImageResolution);
    }
    set generatedImageResolution(pValue) {
        this.setGeneratedImageResolution(pValue);
    }
    /**
     * Creates a new Basket instance.
     * @param pSession Session the basket should operate on.
     * @param pProjectInfo Optional pre-created `ProjectInfo` instance. Will be ignored if `pSession` and `pProjectInfo.session` are not equal.
     */
    constructor(pSession, pProjectInfo) {
        super(pSession);
        this.eventOfmlArticleUpdated = new ListenableEvent(this);
        this.eventOfmlUpdateStateChanged = new ListenableEvent(this);
        /**
         * Will be triggered everytime if items got pasted into the basket. (e.g. copy/paste or OEX import)
         */
        this.eventItemsPasted = new ListenableEvent(this);
        /**
         * Will be triggered if this basket instance gets modified.
         */
        this.eventModified = new ListenableEvent(this);
        /**
         * Will be triggered from `Basket.updateSessionLanguage()`
         */
        this.eventUpdateSessionLanguage = new ListenableEvent(this);
        /**
         * Will be triggered from `Basket.clear()`
         */
        this.eventClear = new ListenableEvent(this);
        this.onNotifyModified = () => {
            this.eventModified.trigger();
        };
        this.mCalcManager = new CalculationManager(this);
        this.mViews = new Array();
        if (pProjectInfo != null) {
            if (pProjectInfo.session === pSession) {
                this.mProjectInfo = pProjectInfo;
            }
            else {
                Log.warn("pProjectInfo parameter will be ignored because its using a different session instance.");
                this.mProjectInfo = new ProjectInfo(pSession);
            }
        }
        else
            this.mProjectInfo = new ProjectInfo(pSession);
        this.mGeneratedImageResolution = 512;
        this.includeCalculationErrorsInInconsistencies = true;
        this.mGeneratedImageOptions = clone(Basket.DEFAULT_GENERATED_IMAGE_OPTIONS);
        //register event handlers to catch all modifications
        this.eventHeaderCalculationChanged.addListener(this.onNotifyModified);
        this.eventItemCreated.addListener(this.onNotifyModified);
        this.eventItemDisposed.addListener(this.onNotifyModified);
        this.eventItemUpdated.addListener(this.onNotifyModified);
        this.eventOfmlArticleUpdated.addListener(this.onNotifyModified);
        this.calculation.eventTaxSchemeChanged.addListener(this.onNotifyModified);
        this.calculation.eventCurrencyChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventAddressDataChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventAppDataChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventContactDataChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventSettingsChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventProjectDataChanged.addListener(this.onNotifyModified);
        this.projectInfo.eventDefaultPriceDateChanged.addListener(this.onNotifyModified);
    }
    async loadFromSession(pOptions) {
        /*
        Do not clear the first time, we do not want to reset the cache of a pre-created ProjectInfo instance.
        See Basket constructor.
        */
        if (this.mRoot != null) {
            this.clear();
        }
        const tPromises = new Array();
        tPromises.push(this.mCalcManager._loadFromSession(pOptions === null || pOptions === void 0 ? void 0 : pOptions.pricingProcedureName));
        tPromises.push(Basket.PrepareBasketColumns(this.mSession));
        tPromises.push(this.loadRootFolder());
        await Promise.all(tPromises);
    }
    /**
     * Should be called if the locale and or data language of the eaiws session has changed to
     * update all language related data.
     */
    async updateSessionLanguage() {
        this.mCalcManager._notifyTaxSchemeChanged();
        this.eventUpdateSessionLanguage.trigger();
        await this.mRoot.updateItem(ItemUpdateFlags.All &
            ~ItemUpdateFlags.Tax &
            ~ItemUpdateFlags.Image &
            ~ItemUpdateFlags.UserFields);
    }
    clear() {
        for (const tView of this.mViews)
            tView._dispose();
        this.eventClear.trigger();
        this.mCalcManager._clear();
        this.mProjectInfo.clear();
        this.mViews = new Array();
        this.internalClear();
    }
    async addView(pViewIdOrConfig) {
        const tView = await View._CreateView(this, pViewIdOrConfig);
        this.mViews.push(tView);
        return (tView);
    }
    /** Removes a view and disposes it. */
    removeView(pView) {
        if (removeElement(this.mViews, pView))
            pView._dispose();
    }
    async fetchOfmlUpdateStates(pItems) {
        //wait while item properties are being fetched, update state is part of props
        await this._fetchItemPropertiesLock.waitWhileLocked();
        //collect main articles with unknown state
        const tMainArticles = new Set();
        for (const tItem of pItems) {
            if (tItem.isOfmlArticle) {
                const tMain = tItem.getMainArticle();
                if (tMain.ofmlUpdateState === "Unknown")
                    tMainArticles.add(tMain.id);
            }
        }
        if (tMainArticles.size === 0)
            return;
        try {
            this._beginItemUpdate();
            //query state
            const tOptions = new basket.UpdateBasketArticlesOptions();
            tOptions.migrate = false;
            tOptions.update = false;
            //split into chunks to prevent timeout error
            const tChunks = chunk(Array.from(tMainArticles), Basket.FETCH_UPDATE_STATE_CHUNK_SIZE);
            const tResults = new Array();
            for (const tChunk of tChunks) {
                const tResult = await this.session.basket.updateBasketArticles(tChunk, null, tOptions);
                tResults.push(...tResult);
            }
            //assign state
            for (const tResult of tResults) {
                const tItem = this.getItemById(tResult.itemId);
                if (tItem != null && tItem instanceof ArticleItem) {
                    tItem._setOfmlUpdateState(tResult.updateState, false);
                }
            }
        }
        finally {
            this._endItemUpdate();
        }
    }
    invalidateHeaderCalculation() {
        super.invalidateHeaderCalculation();
        for (const tView of this.mViews)
            tView.invalidateHeaderCalculation();
    }
    /**
     * Checks the consistency of all articles.
     * Returns false if at least one of the articles or its configuration is inconsistent.
     * Note: Errors of invisible calculation lines will be ignored.
     * @param pCheckCalculation If true the calculation will be also checked for error. (default: true)
     */
    async checkConsistency(pCheckCalculation = true) {
        const checkItem = (pItem) => {
            const tProps = pItem.getCachedItemProperties();
            if (tProps != null && tProps.article != null && tProps.article.inconsistencyFlag === true) {
                return (false);
            }
            for (const tChild of pItem.children) {
                if (!checkItem(tChild)) {
                    return (false);
                }
            }
            return (true);
        };
        //fetch item props for articles first
        await this.mRoot.fetchItemProperties(true, (pItem) => {
            if (pItem === this.mRoot)
                return (false);
            const tType = pItem.type;
            if (tType === "Folder" || tType === "Text" || tType === "UserArticle" ||
                tType === "SetArticle" || tType === "Undefined") {
                return (false);
            }
            return (true);
        });
        //check all items
        if (!checkItem(this.mRoot))
            return (false);
        //check header calculation for errors
        if (pCheckCalculation && this.mCalcManager.pricingProcedure != null) {
            const tCalc = await this.getHeaderCalculation();
            for (const tLine of tCalc.sheet.lines) {
                //ignore invisible lines
                if (tCalc.getLineTags(tLine).has("INVISIBLE"))
                    continue;
                if (tLine.error != null || tLine.groupError != null) {
                    return (false);
                }
            }
        }
        return (true);
    }
    /**
     * Initializes the default generated image options for the given session.
     * The default options will be used from EAIWS if article images are requested
     * (e.g. by calling BasketService.getImages() or BasketService.getItemFields()) but no image
     * was generated yet for the articles.
     */
    static async InitSessionGeneratedImageOptions(pSession) {
        await pSession.basket.getGeneratedImage("", Basket.DEFAULT_GENERATED_IMAGE_OPTIONS);
    }
    /**
     * Create necessary basket columns if they don't exist yet in the provided session.
     * Note: This function will be also called from `Basket.loadFromSession()`.
     */
    static async PrepareBasketColumns(pSession) {
        //check if column exists, if not add it
        const tExistingColumns = await pSession.basket.getBasketColumns();
        const tExistingColumnIds = new Set();
        for (const tCol of tExistingColumns) {
            tExistingColumnIds.add(tCol.id);
        }
        const tColumns = new Array();
        if (!tExistingColumnIds.has(BASKET_COLUMN_ID_USER_IMAGE)) {
            const tColumn = new basket.BasketColumn();
            tColumn.id = BASKET_COLUMN_ID_USER_IMAGE;
            tColumn.type = "Image";
            tColumn.name = "User Image";
            tColumn.title = "User Image";
            tColumn.defaultColumn = "73bd68f4-da62-11d8-b9d6-00e081513ada"; //ArticleImage column
            tColumn.removable = false;
            tColumns.push(tColumn);
        }
        if (!tExistingColumnIds.has(BASKET_COLUMN_ID_EXT_ITEM_NUMBER)) {
            const tColumn = new basket.BasketColumn();
            tColumn.id = BASKET_COLUMN_ID_EXT_ITEM_NUMBER;
            tColumn.type = "Text";
            tColumn.name = "External Item Number";
            tColumn.title = "Ext. Item No.";
            tColumn.removable = false;
            tColumns.push(tColumn);
        }
        if (!tExistingColumnIds.has(BASKET_COLUMN_ID_COMMENTS)) {
            const tColumn = new basket.BasketColumn();
            tColumn.id = BASKET_COLUMN_ID_COMMENTS;
            tColumn.type = "Text";
            tColumn.name = "Comments";
            tColumn.title = "Comments";
            tColumn.removable = false;
            tColumns.push(tColumn);
        }
        for (let i = 0; i < BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length; ++i) {
            if (!tExistingColumnIds.has(BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i])) {
                const tColumn = new basket.BasketColumn();
                tColumn.id = BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i];
                tColumn.type = "Image";
                tColumn.name = "Additional Image " + (i + 1);
                tColumn.title = "Additional Image " + (i + 1);
                tColumn.removable = false;
                tColumns.push(tColumn);
            }
        }
        if (tColumns.length > 0) {
            await pSession.basket.addBasketColumns(tColumns);
        }
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    async _updateViewItems(pChangedBasketItem, pFlags) {
        for (const tView of this.mViews) {
            await tView._updateViewItem(pChangedBasketItem, pFlags);
        }
    }
    /** @internal */
    _getPricingProcedure() {
        return (this.mCalcManager.pricingProcedure);
    }
    /** @internal */
    _getGeneratedImageOptions() {
        return (this.mGeneratedImageOptions);
    }
    /** @internal */
    _getViewConfig() {
        return (null);
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    createItem(pParent, pChildIndex, pEaiwsItem) {
        let tItem;
        switch (pEaiwsItem.itemType) {
            case ("Folder"):
                tItem = new FolderItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("PartialPlanning"):
            case ("Aggregate"):
            case ("Article"):
                tItem = new ArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("UserArticle"):
                tItem = new UserArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("SetArticle"):
                tItem = new SetArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("Text"):
                tItem = new TextItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            default:
                tItem = new BasketItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
        }
        return (tItem);
    }
    setGeneratedImageResolution(pRes) {
        if (pRes === this.mGeneratedImageResolution)
            return;
        this.mGeneratedImageResolution = Math.round(pRes);
        const tNewOption = "width=" + this.mGeneratedImageResolution;
        for (let i = 0; i < this.mGeneratedImageOptions.length; ++i) {
            if (this.mGeneratedImageOptions[i].startsWith("width=")) {
                this.mGeneratedImageOptions[i] = tNewOption;
                return;
            }
        }
        //not found, add new entry
        this.mGeneratedImageOptions.push(tNewOption);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Basket.FETCH_UPDATE_STATE_CHUNK_SIZE = 250;
Basket.DEFAULT_GENERATED_IMAGE_OPTIONS = [
    "format=JPG",
    "shadowPlane=true",
    "shadowPlane.filter=DOF",
    "shadowPlane.color=0.4 0.4 0.4",
    "ambient=0.8 0.8 0.8",
    "renderMode=PBR"
];
